'use client'

import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import Header from '@/components/Header'
import HeroParallax from '@/components/HeroParallax'
import Footer from '@/components/Footer'
import { getAdventureById } from '@/lib/data'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/free-mode'
import styles from '@/styles/adventure-page.module.css'
import { BreadcrumbSchema, AdventureSchema } from '@/lib/schema'
import { useSwiperCleanup } from '@/hooks/useSwiperCleanup'
import GoogleMapEmbed, { GOOGLE_MAPS_EMBEDS } from '@/components/GoogleMapEmbed'


export default function CatSkiingPage() {
  const swiperRef = useSwiperCleanup()
  const thisAdventure = getAdventureById('cat-skiing')
  const t = useTranslations('guidedAdventures')
  const tAdv = useTranslations('adventures')
  const tCommon = useTranslations('common')
  const tLodges = useTranslations('lodges')
  const params = useParams()
  const locale = params.locale as string

  if (!thisAdventure) {
    return <div>{t('common.adventureNotFound')}</div>
  }

  return (
    <>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: `/${locale}/` },
        { name: t('index.title'), href: `/${locale}/guided-adventures/` },
        { name: tAdv('cat-skiing.title'), href: `/${locale}/guided-adventures/cat-skiing/` },
      ]} />
      <AdventureSchema
        name={t('catSkiing.schema.name')}
        description={t('catSkiing.schema.description')}
        image={thisAdventure.heroImage}
        url={`/${locale}/guided-adventures/cat-skiing/`}
        activityType={t('catSkiing.schema.activityType')}
        location={t('catSkiing.schema.location')}
        geo={{ latitude: 38.87, longitude: -106.99 }}
      />
      <Header />

      <HeroParallax
        backgroundImage={thisAdventure.heroImage}
        title={tAdv('cat-skiing.title')}
        subtitle={tAdv('cat-skiing.subtitle')}
        location={tAdv('cat-skiing.location')}
      />

      <main className={styles.pageWrapper}>
        <section className={styles.adventureDetails}>
          <div className="container">
            <div className={styles.detailsGrid}>
              <div className={styles.itineraryColumn}>
                <h3>{t('common.itinerary')}</h3>
                <p className={styles.duration}>{t('catSkiing.duration')}</p>

                <div className={styles.itineraryItem}>
                  <strong>{t('common.day')} 1:</strong> {t('catSkiing.day1')}
                </div>

                <div className={styles.itineraryItem}>
                  <strong>{t('common.day')} 2-3:</strong> {t('catSkiing.day2to3')}
                </div>

                <div className={styles.itineraryItem}>
                  <strong>{t('common.day')} 4:</strong> {t('catSkiing.day4')}
                </div>
              </div>

              <div className={styles.mapColumn}>
                <div className={styles.mapWrapper}>
                  <GoogleMapEmbed
                    {...GOOGLE_MAPS_EMBEDS['crested-butte']}
                    
                  />
                </div>
              </div>

              <div className={styles.includedColumn}>
                <h3>{t('common.whatsIncluded')}</h3>
                <ul>
                  <li>{t('catSkiing.included.item1')}</li>
                  <li>{t('catSkiing.included.item2')}</li>
                  <li>{t('catSkiing.included.item3')}</li>
                  <li>{t('catSkiing.included.item4')}</li>
                  <li>{t('catSkiing.included.item5')}</li>
                  <li>{t('catSkiing.included.item6')}</li>
                  <li>{t('catSkiing.included.item7')}</li>
                  <li>{t('catSkiing.included.item8')}</li>
                  <li>{t('catSkiing.included.item9')}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.adventures}>
          <div className="container">
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t('common.personalizedAdventures')}</h2>
              <p className={styles.sectionDescription}>
                {t('common.personalizedDescription')}
              </p>
            </div>

            <div className={styles.adventuresCarousel}>
              <Swiper
                modules={[Navigation]}
                slidesPerView={1}
                spaceBetween={24}
                loop={true}
                grabCursor={true}
                navigation={{
                  nextEl: `.${styles.swiperButtonNext}`,
                  prevEl: `.${styles.swiperButtonPrev}`,
                }}
                breakpoints={{
                  640: { slidesPerView: 2, spaceBetween: 24 },
                  1024: { slidesPerView: 3, spaceBetween: 32 },
                }}
                onSwiper={(swiper) => { swiperRef.current = swiper }}
                className={styles.adventuresSwiper}
              >
                {[
                  { id: 1, src: '/images/adventures/cat-skiing/1.webp', alt: t('catSkiing.images.img1') },
                  { id: 2, src: '/images/adventures/cat-skiing/2.webp', alt: t('catSkiing.images.img2') },
                  { id: 3, src: '/images/adventures/cat-skiing/3.webp', alt: t('catSkiing.images.img3') },
                  { id: 4, src: '/images/adventures/cat-skiing/4.webp', alt: t('catSkiing.images.img4') },
                ].map((image) => (
                  <SwiperSlide key={`cat-skiing-${image.id}`}>
                    <article className={styles.adventureCard}>
                      <div className={styles.adventureImage}>
                        <img src={image.src} alt={image.alt} loading="lazy" />
                      </div>
                    </article>
                  </SwiperSlide>
                ))}
              </Swiper>

              <div className={`${styles.swiperButtonPrev} swiper-button-prev`}></div>
              <div className={`${styles.swiperButtonNext} swiper-button-next`}></div>
            </div>

            <div className={styles.sectionCta}>
              <a href={`/${locale}/guided-adventures/`} className="btn-primary">
                {t('common.exploreAdventures')} &rarr;
              </a>
            </div>
          </div>
        </section>

        <section className={styles.creamSection}>
          <div className="container">
            <h2>{t('common.whereToStay')}</h2>
            <div className={styles.lodgesGrid}>
              <article className={styles.lodgeCard}>
                <div className={styles.lodgeImage}>
                  <img
                    src="/images/lodges/apex-mountain-penthouse/IMG_2891.webp"
                    alt={`${tLodges('apex-mountain-penthouse.name')} - ${tLodges('apex-mountain-penthouse.subtitle')}`}
                    loading="lazy"
                  />
                </div>
                <div className={styles.lodgeOverlay}>
                  <h3 className={styles.lodgeTitle}>{tLodges('apex-mountain-penthouse.name')}</h3>
                  <div className={styles.lodgeContent}>
                    <p>{tLodges('apex-mountain-penthouse.subtitle')}</p>
                    <a href={`/${locale}/lodges/apex-mountain-penthouse/`} className={styles.lodgeLink}>
                      {t('common.explore')} &rarr;
                    </a>
                  </div>
                </div>
              </article>

              <article className={styles.lodgeCard}>
                <div className={styles.lodgeImage}>
                  <img
                    src="/images/lodges/apex-yelcho-patagonia/yelcho-en-la-patagonia_exterior_hotel_1-1-qtpgs4svvtox39co7ll9fi4dr08v34gfk5agnwmmq0.avif"
                    alt={`${tLodges('apex-yelcho-patagonia.name')} - ${tLodges('apex-yelcho-patagonia.subtitle')}`}
                    loading="lazy"
                  />
                </div>
                <div className={styles.lodgeOverlay}>
                  <h3 className={styles.lodgeTitle}>{tLodges('apex-yelcho-patagonia.name')}</h3>
                  <div className={styles.lodgeContent}>
                    <p>{tLodges('apex-yelcho-patagonia.subtitle')}</p>
                    <a href={`/${locale}/lodges/apex-yelcho-patagonia/`} className={styles.lodgeLink}>
                      {t('common.explore')} &rarr;
                    </a>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </>
  )
}
